// RandomCubes shader: Creates a 3D isometric cube grid with realistic depth and lighting
const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_seed;
uniform float u_speed;

uniform float u_scale;        // Grid density (recommend 8-30)
uniform float u_depth;        // Z-axis depth variation (0..1)
uniform float u_tilt;         // Isometric tilt angle (0..1)
uniform float u_bevel;        // Edge bevel amount (0..0.5)
uniform float u_gap;          // Space between cubes (0..0.3)
uniform float u_lightAngle;   // Light direction (0..6.28)
uniform float u_ambient;      // Ambient light (0..1)

uniform vec3 u_color_0;       // Primary cube color
uniform vec3 u_color_1;       // Secondary cube color  
uniform vec3 u_color_2;       // Accent color
uniform vec3 u_color_3;       // Background/shadow color

varying vec2 vUv;

// Enhanced hash function for better randomness
float hash(vec2 p) {
    p += u_seed;
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

vec2 hash2(vec2 p) {
    p += u_seed;
    return fract(sin(vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)))) * 43758.5453123);
}

// Isometric projection helper
vec2 isoProject(vec3 pos) {
    float tilt = u_tilt * 0.6; // Convert to radians
    return vec2(
        pos.x - pos.z * cos(tilt),
        pos.y - pos.z * sin(tilt)
    );
}

// 3D cube face calculation
vec3 getCubeFace(vec2 uv, vec3 pos) {
    vec2 center = vec2(0.5);
    vec2 localUV = uv - center;
    
    // Determine which face we're looking at based on position
    float faceType = step(0.5, abs(localUV.x)) + step(0.5, abs(localUV.y)) * 2.0;
    
    // Calculate lighting based on face normal and light direction
    vec3 normal;
    if (faceType < 1.0) {
        normal = vec3(0.0, 0.0, 1.0); // Top face
    } else if (faceType < 2.0) {
        normal = vec3(sign(localUV.x), 0.0, 0.0); // Side faces
    } else {
        normal = vec3(0.0, sign(localUV.y), 0.0); // Front/back faces
    }
    
    // Light calculation
    vec3 lightDir = vec3(cos(u_lightAngle), sin(u_lightAngle), 0.8);
    lightDir = normalize(lightDir);
    float NdotL = max(0.0, dot(normal, lightDir));
    
    // Height-based color variation
    float heightFactor = (pos.z + 1.0) * 0.5;
    vec3 baseColor = mix(u_color_0, u_color_1, heightFactor);
    baseColor = mix(baseColor, u_color_2, hash(pos.xy) * 0.3);
    
    // Apply lighting
    float lighting = u_ambient + (1.0 - u_ambient) * NdotL;
    
    // Face-specific shading
    if (faceType < 1.0) {
        lighting *= 1.0; // Top face - brightest
    } else if (faceType < 2.0) {
        lighting *= 0.7; // Side faces - medium
    } else {
        lighting *= 0.5; // Front/back faces - darkest
    }
    
    return baseColor * lighting;
}

void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution;
    float aspect = u_resolution.x / u_resolution.y;
    
    // Create grid with proper aspect ratio
    float gridSize = max(4.0, u_scale);
    vec2 gridUV = uv * vec2(gridSize * aspect, gridSize);
    
    // Add time-based animation for dynamic height
    float timeOffset = (u_time * u_speed) * 0.1;
    
    vec2 cellID = floor(gridUV);
    vec2 cellUV = fract(gridUV);
    
    // Generate random height for each cube
    float noise = hash(cellID);
    float animatedNoise = hash(cellID + vec2(timeOffset));
    float height = mix(noise, animatedNoise, 0.3) * u_depth;
    
    // Create 3D position for the cube
    vec3 cubePos = vec3(cellID, height * 2.0 - 1.0);
    
    // Apply isometric projection
    vec2 isoUV = isoProject(cubePos);
    vec2 projectedUV = cellUV + (isoUV - cellID) * 0.1;
    
    // Gap calculation
    float gap = clamp(u_gap, 0.0, 0.3);
    vec2 gapMask = smoothstep(gap, gap + 0.01, cellUV) * 
                   smoothstep(gap, gap + 0.01, 1.0 - cellUV);
    float mask = gapMask.x * gapMask.y;
    
    // Bevel effect
    float bevel = clamp(u_bevel, 0.0, 0.4);
    vec2 bevelMask = smoothstep(0.0, bevel, cellUV) * 
                     smoothstep(0.0, bevel, 1.0 - cellUV);
    float bevelFactor = min(bevelMask.x, bevelMask.y);
    
    // Get cube face color
    vec3 cubeColor = getCubeFace(cellUV, cubePos);
    
    // Apply bevel shading
    cubeColor = mix(cubeColor * 0.6, cubeColor, bevelFactor);
    
    // Apply gap mask
    cubeColor *= mask;
    
    // Background color where there are gaps
    vec3 finalColor = mix(u_color_3, cubeColor, mask);
    
    // Add subtle depth fog
    float distanceFromCenter = length(uv - 0.5);
    float fog = smoothstep(0.8, 0.3, distanceFromCenter);
    finalColor = mix(u_color_3 * 0.5, finalColor, fog);
    
    gl_FragColor = vec4(finalColor, 1.0);
}
`;

const shader = {
  name: 'RandomCubes',
  shader: { frag },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 4,
      max: 4,
      default: ['#2c3e50', '#3498db', '#e74c3c', '#34495e'],
    },
    {
      key: 'u_scale',
      type: 'number',
      label: 'Grid Density',
      min: 4,
      max: 50,
      step: 1,
      default: 16,
    },
    {
      key: 'u_depth',
      type: 'number',
      label: 'Height Variation',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.6,
    },
    {
      key: 'u_tilt',
      type: 'number',
      label: 'Isometric Tilt',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
    {
      key: 'u_bevel',
      type: 'number',
      label: 'Edge Bevel',
      min: 0,
      max: 0.4,
      step: 0.01,
      default: 0.1,
    },
    {
      key: 'u_gap',
      type: 'number',
      label: 'Cube Spacing',
      min: 0,
      max: 0.3,
      step: 0.005,
      default: 0.04,
    },
    {
      key: 'u_lightAngle',
      type: 'number',
      label: 'Light Angle',
      min: 0,
      max: 6.28,
      step: 0.1,
      default: 2.4,
    },
    {
      key: 'u_ambient',
      type: 'number',
      label: 'Ambient Light',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.3,
    },
    { key: 'u_speed', type: 'number', label: 'Speed', min: 0, max: 3, step: 0.1, default: 0.5 },
    {
      key: 'u_seed',
      type: 'number',
      label: 'Random Seed',
      min: 0,
      max: 999,
      step: 1,
      default: 42,
    },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(RandomCubes, colors=#1a1a2e,#16213e,#0f3460,#18fa58; u_scale=8; u_depth=0.67; u_tilt=0.7; u_bevel=0.08; u_gap=0.09; u_lightAngle=4.5; u_ambient=0.59; u_speed=1.8; u_seed=42; animated=false)',
    'dynamic-shader(RandomCubes, colors=#1a1a2e,#16213e,#0f3460,#e94560; u_scale=20; u_tilt=0.7; u_lightAngle=1.5; animated=true)',
    'dynamic-shader(RandomCubes, colors=#ff6b6b,#ffd93d,#6bcf7f,#4d4d4d; u_gap=0.08; u_bevel=0.15; u_ambient=0.4; animated=false)',
  ],
};

export default shader;
